import React, { useContext, useState, useEffect } from 'react';
import ProductCard from './ProductCard';
import Loader from '../shared/Loader';
import { ProductContext } from '../../providers/ProductContext';
import { CategoryContext } from '../../providers/CategoryContext';
import { useSearchParams } from 'react-router-dom';
import SEO from '../shared/SEO';
import { ConfigContext } from '../../providers/ConfigContext';


const ProductList = () => {
  const { products, loading: productsLoading } = useContext(ProductContext);
  const { categories, loading: categoriesLoading } = useContext(CategoryContext);
  const { config } = useContext(ConfigContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [storeStatsEnabled, setStoreStatsEnabled] = useState(true);
  const productsPerPage = 20;

  useEffect(() => {
    const loadStoreStatsSetting = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/store-config`);
        const data = await res.json();
        if (res.ok && data.success) {
          setStoreStatsEnabled(data.data?.storeStatsEnabled !== false);
        }
      } catch (error) {
        console.error('Error loading store config:', error);
      }
    };

    loadStoreStatsSetting();
  }, []);

  // Se leen los parámetros de la URL
  const selectedCategory = searchParams.get('category') || '';
  const selectedSubcategory = searchParams.get('subcategory') || '';

  // Solo mostrar productos con categoría válida y activa
  const activeProducts = products.filter(p => p.category && p.category.name);

  const filteredProducts = activeProducts.filter(p => 
    (!selectedCategory || p.category.name === selectedCategory) &&
    (!selectedSubcategory || p.subcategory === selectedSubcategory) &&
    (!searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Calcular paginación
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const endIndex = startIndex + productsPerPage;
  const currentProducts = filteredProducts.slice(startIndex, endIndex);

  // Resetear a página 1 cuando cambien los filtros o búsqueda
  useEffect(() => {
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedCategory, selectedSubcategory, searchQuery]);

  // Función para cambiar de página
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (categoriesLoading || productsLoading) {
    return <Loader />;
  }

  // Generar título y descripción SEO dinámicos según filtros
  const getSeoTitle = () => {
    if (selectedSubcategory) {
      return selectedSubcategory;
    }
    if (selectedCategory) {
      return selectedCategory;
    }
    return 'Tienda';
  };

  const getSeoDescription = () => {
    if (selectedCategory) {
      return `Explora nuestra colección de ${selectedCategory.toLowerCase()}. ${filteredProducts.length} productos disponibles con envío a todo Uruguay.`;
    }
    return `Descubre ${filteredProducts.length} productos de moda femenina en ${config?.brand?.name || 'E-MOREIRA'}. Las últimas tendencias con envíos a todo Uruguay.`;
  };

  return (
    <>
      <SEO 
        title={getSeoTitle()}
        description={getSeoDescription()}
        url={`/tienda${selectedCategory ? `?category=${selectedCategory}` : ''}`}
        type="website"
        siteName={config?.brand?.name}
        breadcrumbs={[
          { name: 'Inicio', url: '/' },
          { name: 'Tienda', url: '/tienda' },
          ...(selectedCategory ? [{ name: selectedCategory, url: `/tienda?category=${selectedCategory}` }] : []),
          ...(selectedSubcategory ? [{ name: selectedSubcategory, url: `/tienda?category=${selectedCategory}&subcategory=${selectedSubcategory}` }] : [])
        ]}
      />
      <div className="store-container">
        {/* Store Header */}
      <div className="store-header">
        <div className="container">
          <div className="row align-items-center">
            <div className={storeStatsEnabled ? 'col-md-8' : 'col-12'}>
              <h1 className="store-title">
                <i className="fa-solid fa-store me-3"></i>
                Nuestra Tienda
              </h1>
              <p className="store-subtitle">
                Descubre productos únicos y de calidad
              </p>
            </div>
            {storeStatsEnabled && (
              <div className="col-md-4 text-md-end">
                <div className="store-stats">
                  <div className="stat-item">
                    <span className="stat-number">{filteredProducts.length}</span>
                    <span className="stat-label">Productos</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number">{categories.length}</span>
                    <span className="stat-label">Categorías</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          {/* Enhanced Sidebar */}
          <div className="col-lg-3 col-md-4">
            <div className="store-sidebar">
              <div className="sidebar-header">
                <h4>
                  <i className="fa-solid fa-filter me-2"></i>
                  Filtrar Productos
                </h4>
                {(selectedCategory || selectedSubcategory) && (
                  <button 
                    className="btn-clear-filters"
                    onClick={() => setSearchParams({})}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                )}
              </div>

              {/* All Products Button */}
              <div className="filter-section">
                <button 
                  className={`filter-btn-main ${!selectedCategory ? 'active' : ''}`}
                  onClick={() => setSearchParams({})}
                >
                  <i className="fa-solid fa-grid-2 me-2"></i>
                  <span>Todos los Productos</span>
                  <span className="product-count">{activeProducts.length}</span>
                </button>
              </div>

              {/* Categories */}
              <div className="filter-section">
                <h5 className="section-title">
                  <i className="fa-solid fa-tags me-2"></i>
                  Categorías
                </h5>
                <div className="category-list">
                  {categories.map(category => {
                    const count = activeProducts.filter(p => p.category.name === category.name).length;
                    const isActive = selectedCategory === category.name;
                    
                    return (
                      <div key={category._id} className="category-item">
                        <button
                          className={`category-btn ${isActive ? 'active' : ''}`}
                          onClick={() => setSearchParams({ category: category.name })}
                        >
                          <div className="category-info">
                            <i className={`${category.icon} category-icon`}></i>
                            <span className="category-name">{category.name}</span>
                          </div>
                          <span className="category-count">{count}</span>
                        </button>
                        
                        {/* Subcategories */}
                        {isActive && category.subcategories?.length > 0 && (
                          <div className="subcategory-list">
                            {category.subcategories.map(subcategory => {
                              const subCount = activeProducts.filter(p => p.category.name === category.name && p.subcategory === subcategory.name).length;
                              const isSubActive = selectedSubcategory === subcategory.name;
                              
                              return (
                                <button
                                  key={subcategory.name}
                                  className={`subcategory-btn ${isSubActive ? 'active' : ''}`}
                                  onClick={() => setSearchParams({ category: category.name, subcategory: subcategory.name })}
                                >
                                  <span className="subcategory-name">{subcategory.name}</span>
                                  <span className="subcategory-count">{subCount}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Active Filters */}
              {(selectedCategory || selectedSubcategory) && (
                <div className="active-filters">
                  <h6 className="filters-title">
                    <i className="fa-solid fa-check-circle me-2"></i>
                    Filtros Activos
                  </h6>
                  <div className="filter-tags">
                    {selectedCategory && (
                      <span className="filter-tag category-tag">
                        <i className="fa-solid fa-tag me-1"></i>
                        {selectedCategory}
                        <button 
                          className="remove-filter"
                          onClick={() => setSearchParams(selectedSubcategory ? { subcategory: selectedSubcategory } : {})}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    )}
                    {selectedSubcategory && (
                      <span className="filter-tag subcategory-tag">
                        <i className="fa-solid fa-bookmark me-1"></i>
                        {selectedSubcategory}
                        <button 
                          className="remove-filter"
                          onClick={() => setSearchParams(selectedCategory ? { category: selectedCategory } : {})}
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Products Grid */}
          <div className="col-lg-9 col-md-8">
            <div className="products-section">
              {/* Results Header con Buscador */}
              <div className="results-header-with-search">
                <div className="row align-items-center">
                  {/* Título y contador - 50% */}
                  <div className="col-md-6">
                    <div className="results-info">
                      <h3 className="results-title">
                        {selectedSubcategory ? (
                          <>
                            <i className="fa-solid fa-bookmark me-2"></i>
                            {selectedSubcategory}
                          </>
                        ) : selectedCategory ? (
                          <>
                            <i className="fa-solid fa-tag me-2"></i>
                            {selectedCategory}
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-grid-2 me-2"></i>
                            Todos los Productos
                          </>
                        )}
                        {config?.brand?.name && (
                          <span className="results-title-brand"> | {config.brand.name}</span>
                        )}
                      </h3>
                      <p className="results-count">
                        {filteredProducts.length === 0 ? (
                          'No se encontraron productos'
                        ) : (
                          <>
                            {`${filteredProducts.length} producto${filteredProducts.length !== 1 ? 's' : ''} encontrado${filteredProducts.length !== 1 ? 's' : ''}`}
                            {totalPages > 1 && (
                              <span className="page-indicator ms-2">
                                (Página {currentPage} de {totalPages})
                              </span>
                            )}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  {/* Buscador - 50% */}
                  <div className="col-md-6">
                    <div className="store-search-wrapper">
                      <i className="fa-solid fa-search search-icon"></i>
                      <input
                        type="text"
                        className="store-search-input"
                        placeholder="Buscar productos..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button 
                          className="search-clear-btn"
                          onClick={() => setSearchQuery('')}
                          aria-label="Limpiar búsqueda"
                        >
                          <i className="fa-solid fa-times"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Products Grid */}
              {filteredProducts.length === 0 ? (
                <div className="products-grid store-products-grid">
                  <div className="no-products">
                    <div className="no-products-content">
                      <i className="fa-solid fa-search"></i>
                      <h4>No hay productos en esta categoría</h4>
                      <p>Intenta con otra categoría o explora todos nuestros productos</p>
                      <button 
                        className="btn-explore-all"
                        onClick={() => setSearchParams({})}
                      >
                        <i className="fa-solid fa-grid-2 me-2"></i>
                        Ver Todos los Productos
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="products-grid store-products-grid">
                    {currentProducts.map(product => (
                      <div key={product._id} className="product-item-enhanced">
                        <ProductCard product={product} />
                      </div>
                    ))}
                  </div>

                  {/* Paginación */}
                  {totalPages > 1 && (
                    <div className="ecommerce-pagination">
                      <button 
                        className="pagination-btn prev-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fa-solid fa-chevron-left"></i>
                        <span>Anterior</span>
                      </button>

                      <div className="pagination-numbers">
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Mostrar solo algunas páginas alrededor de la actual
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                className={`pagination-number ${currentPage === pageNumber ? 'active' : ''}`}
                                onClick={() => handlePageChange(pageNumber)}
                              >
                                {pageNumber}
                              </button>
                            );
                          } else if (
                            pageNumber === currentPage - 2 ||
                            pageNumber === currentPage + 2
                          ) {
                            return <span key={pageNumber} className="pagination-dots">...</span>;
                          }
                          return null;
                        })}
                      </div>

                      <button 
                        className="pagination-btn next-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <span>Siguiente</span>
                        <i className="fa-solid fa-chevron-right"></i>
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
};

export default ProductList;
